import React, { useState, useEffect, useRef, useCallback } from "react";
import { analytics } from "../../lib/analytics";
import styles from "./VisualCountdownTimer.module.scss";

export interface VisualCountdownTimerProps {
  duration: number; // seconds
  onComplete?: () => void;
  onTick?: (timeLeft: number) => void;
  size?: "small" | "medium" | "large";
  color?: string;
  autoStart?: boolean;
  showControls?: boolean;
  title?: string;
}

export const VisualCountdownTimer: React.FC<VisualCountdownTimerProps> = ({
  duration,
  onComplete,
  onTick,
  size = "medium",
  color = "#4CAF50",
  autoStart = false,
  showControls = true,
  title,
}) => {
  const [timeLeft, setTimeLeft] = useState(duration);
  const [isRunning, setIsRunning] = useState(autoStart);
  // Remove unused progress state - progress is calculated dynamically
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const startTimeRef = useRef<number>(Date.now());

  // Calculate progress (0 to 1)
  const progressValue = duration > 0 ? (duration - timeLeft) / duration : 0;

  // Format time display
  const formatTime = (seconds: number): string => {
    const minutes = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${minutes}:${secs.toString().padStart(2, "0")}`;
  };

  // Timer tick function
  const tick = useCallback(() => {
    setTimeLeft(prevTime => {
      const newTime = Math.max(0, prevTime - 1);

      // Call onTick callback
      onTick?.(newTime);

      // Check if timer is complete
      if (newTime === 0) {
        setIsRunning(false);

        // Track focus session completed
        const sessionDuration = Math.floor((Date.now() - startTimeRef.current) / 1000 / 60); // in minutes
        analytics.track("focus_session_completed", {
          session_type:
            duration >= 1500 ? "pomodoro" : duration >= 300 ? "timeblock" : "short_focus", // 25min, 5min thresholds
          session_id: `session_${startTimeRef.current}`, // Simple session ID
          actual_duration: sessionDuration,
          planned_duration: Math.floor(duration / 60),
          completion_rate: sessionDuration / Math.floor(duration / 60),
          interruption_count: 0,
          tasks_completed: 1, // Default, could be enhanced
          break_taken: 0, // Default, could be enhanced
          productivity_score: sessionDuration >= (duration / 60) * 0.8 ? 5 : 3, // Simple scoring
        });

        onComplete?.();
      }

      return newTime;
    });
  }, [onTick, onComplete, duration]);

  // Start timer
  const startTimer = useCallback(() => {
    if (timeLeft > 0 && !isRunning) {
      setIsRunning(true);
      startTimeRef.current = Date.now();

      // Track focus session started
      analytics.track("focus_session_started", {
        session_type: duration >= 1500 ? "pomodoro" : duration >= 300 ? "timeblock" : "short_focus", // 25min, 5min thresholds
        planned_duration: Math.floor(duration / 60), // in minutes
        task_count: 1, // Assume 1 task for now, could be enhanced
        energy_level: "medium", // Default medium energy, could be enhanced
        time_of_day: new Date().getHours(),
        day_of_week: new Date().getDay(),
      });
    }
  }, [timeLeft, isRunning, duration]);

  // Pause timer
  const pauseTimer = useCallback(() => {
    setIsRunning(false);
  }, []);

  // Reset timer
  const resetTimer = useCallback(() => {
    setIsRunning(false);
    setTimeLeft(duration);
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, [duration]);

  // Toggle play/pause
  const toggleTimer = useCallback(() => {
    if (isRunning) {
      pauseTimer();
    } else {
      startTimer();
    }
  }, [isRunning, startTimer, pauseTimer]);

  // Effect to manage timer interval
  useEffect(() => {
    if (isRunning && timeLeft > 0) {
      intervalRef.current = setInterval(tick, 1000);
    } else {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [isRunning, timeLeft, tick]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, []);

  // Calculate SVG dimensions based on size
  const getDimensions = () => {
    switch (size) {
      case "small":
        return { width: 120, height: 120, radius: 50, strokeWidth: 6 };
      case "large":
        return { width: 280, height: 280, radius: 130, strokeWidth: 8 };
      default:
        return { width: 200, height: 200, radius: 90, strokeWidth: 8 };
    }
  };

  const { width, height, radius, strokeWidth } = getDimensions();
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset = circumference * (1 - progressValue);

  // Determine color based on time remaining
  const getTimerColor = () => {
    const percentRemaining = timeLeft / duration;
    if (percentRemaining > 0.5) return color;
    if (percentRemaining > 0.2) return "#FF9800"; // Orange
    return "#F44336"; // Red
  };

  return (
    <div
      className={`${styles.timerContainer} ${styles[size]}`}
      data-testid="visual-countdown-timer"
    >
      {title && <div className={styles.title}>{title}</div>}

      <div className={styles.timerWrapper}>
        <svg
          className={styles.progressRing}
          width={width}
          height={height}
          data-testid="progress-ring"
        >
          {/* Background circle */}
          <circle
            className={styles.progressRingBackground}
            cx={width / 2}
            cy={height / 2}
            r={radius}
            strokeWidth={strokeWidth}
            fill="transparent"
          />

          {/* Progress circle */}
          <circle
            className={styles.progressRingCircle}
            cx={width / 2}
            cy={height / 2}
            r={radius}
            stroke={getTimerColor()}
            strokeWidth={strokeWidth}
            fill="transparent"
            strokeLinecap="round"
            style={{
              strokeDasharray: circumference,
              strokeDashoffset: strokeDashoffset,
              transform: "rotate(-90deg)",
              transformOrigin: `${width / 2}px ${height / 2}px`,
            }}
            data-testid="progress-arc"
          />
        </svg>

        <div className={styles.timeDisplay} data-testid="time-display">
          {formatTime(timeLeft)}
        </div>

        {isRunning && (
          <div className={styles.pulseIndicator} data-testid="pulse-indicator">
            <div className={styles.pulse}></div>
          </div>
        )}
      </div>

      {showControls && (
        <div className={styles.controls} data-testid="timer-controls">
          <button
            className={`${styles.controlButton} ${styles.playPause}`}
            onClick={toggleTimer}
            disabled={timeLeft === 0}
            data-testid="play-pause-button"
            aria-label={isRunning ? "Pause timer" : "Start timer"}
          >
            {isRunning ? "⏸️" : "▶️"}
          </button>

          <button
            className={`${styles.controlButton} ${styles.reset}`}
            onClick={resetTimer}
            data-testid="reset-button"
            aria-label="Reset timer"
          >
            🔄
          </button>
        </div>
      )}

      {/* Screen reader announcements */}
      <div
        className={styles.srOnly}
        aria-live="polite"
        aria-atomic="true"
        data-testid="sr-announcer"
      >
        {isRunning
          ? `Timer running: ${formatTime(timeLeft)} remaining`
          : timeLeft === 0
            ? "Timer completed"
            : `Timer paused: ${formatTime(timeLeft)} remaining`}
      </div>
    </div>
  );
};
